import { useEffect, useRef, useState } from "react";
import { CsvImportBatch } from "@/lib/api";
import { CsvImportPlan } from "@/lib/csvImportBatches";
import { getCustomerRegistrationErrorMessage } from "@/lib/customerRegistrationError";
import { validateImportFile, withRetry } from "./csvImport";

export type CsvImportMode = "preview" | "confirm";

export interface CsvImportFileInfo {
  fileName: string;
  fileSize: number;
  totalBatches: number;
}

/**
 * O que muda entre as importações (grupos e grupos por cliente); o ciclo de lotes é o mesmo. TBatch é a resposta de um
 * lote e TResult o resultado somado, que pode ter campos a mais calculados em finish.
 */
export interface CsvBatchImportConfig<TResult, TPlan extends CsvImportPlan, TBatch = TResult> {
  plan: (content: string) => TPlan | null;
  emptyResult: (dryRun: boolean) => TResult;
  merge: (total: TResult, batch: TBatch) => TResult;
  sendBatch: (batch: CsvImportBatch, dryRun: boolean, signal: AbortSignal) => Promise<TBatch>;
  /** Fecha o resultado depois do último lote (ordenar, calcular o que depende do arquivo inteiro). */
  finish: (result: TResult, plan: TPlan) => TResult | Promise<TResult>;
  /** Resumo no histórico; a API grava uma vez por importId, então repetir é seguro. */
  complete: (importId: string, file: CsvImportFileInfo, result: TResult, signal: AbortSignal) => Promise<unknown>;
  onSaved: (result: TResult) => void | Promise<void>;
}

/**
 * Execução da prévia ou da gravação, lote a lote. nextBatch é o índice do próximo lote a enviar. "failed" e
 * "cancelled" guardam onde pararam, para retomar.
 */
export interface CsvImportRun<TResult, TPlan extends CsvImportPlan> {
  mode: CsvImportMode;
  importId: string;
  fileName: string;
  fileSize: number;
  plan: TPlan;
  nextBatch: number;
  result: TResult;
  /** Linhas já enviadas, para o andamento. */
  sentLines: number;
  status: "running" | "failed" | "cancelled";
  error: string | null;
}

/**
 * Lê o CSV na tela e envia em lotes, um de cada vez, com até 3 tentativas por lote. Se um lote falhar ou o usuário
 * cancelar, para e guarda onde parou: resume() continua dali com o mesmo importId. Cancelar aborta a requisição em
 * andamento; se a API já tinha gravado aquele lote, retomar reenvia e ele volta como inalterado, porque a API não
 * regrava o que já está igual. Pelo mesmo motivo, reenviar o arquivo inteiro não duplica nada. Na gravação, depois
 * do último lote, grava o resumo.
 */
export function useCsvBatchImport<TResult, TPlan extends CsvImportPlan, TBatch = TResult>(
  config: CsvBatchImportConfig<TResult, TPlan, TBatch>,
) {
  const configRef = useRef(config);
  configRef.current = config;
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<TPlan | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [run, setRun] = useState<CsvImportRun<TResult, TPlan> | null>(null);
  const [preview, setPreview] = useState<TResult | null>(null);
  const [saved, setSaved] = useState<TResult | null>(null);
  // Cada execução tem o seu AbortController; trocar de arquivo, cancelar, limpar ou sair da tela aborta a atual.
  const controllerRef = useRef<AbortController | null>(null);

  const stopCurrent = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  };

  useEffect(() => stopCurrent, []);

  const execute = async (start: CsvImportRun<TResult, TPlan>) => {
    const { sendBatch, finish, complete, onSaved } = configRef.current;
    stopCurrent();
    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;
    const { plan: runPlan } = start;
    const totalBatches = runPlan.batches.length;
    let current: CsvImportRun<TResult, TPlan> = { ...start, status: "running", error: null };
    setRun(current);

    const fail = (error: string) => {
      if (signal.aborted) return;
      controllerRef.current = null;
      setRun({ ...current, status: "failed", error });
    };

    while (current.nextBatch < totalBatches) {
      const index = current.nextBatch;
      try {
        const batchResult = await withRetry(
          () =>
            sendBatch(
              {
                importId: current.importId,
                fileName: current.fileName,
                batchNumber: index + 1,
                totalBatches,
                header: runPlan.header,
                lines: runPlan.batches[index],
              },
              current.mode === "preview",
              signal,
            ),
          signal,
        );
        if (signal.aborted) return;
        current = {
          ...current,
          nextBatch: index + 1,
          result: configRef.current.merge(current.result, batchResult),
          sentLines: current.sentLines + runPlan.batches[index].length,
        };
        setRun(current);
      } catch (error) {
        const message = getCustomerRegistrationErrorMessage(error, "Falha de comunicação com o servidor.");
        fail(`Lote ${index + 1} de ${totalBatches}: ${message}`);
        return;
      }
    }

    let result: TResult;
    try {
      result = await finish(current.result, runPlan);
    } catch (error) {
      const message = getCustomerRegistrationErrorMessage(error, "Falha de comunicação com o servidor.");
      fail(`Todos os lotes foram processados, mas não deu para fechar o resultado: ${message}`);
      return;
    }
    if (signal.aborted) return;

    if (current.mode === "preview") {
      controllerRef.current = null;
      setPreview(result);
      setRun(null);
      return;
    }

    try {
      await withRetry(
        () => complete(current.importId, { fileName: current.fileName, fileSize: current.fileSize, totalBatches }, result, signal),
        signal,
      );
    } catch {
      fail("Todos os lotes foram gravados, mas o resumo não entrou no histórico. Clique em Tentar de novo.");
      return;
    }
    if (signal.aborted) return;

    controllerRef.current = null;
    setSaved(result);
    setPreview(null);
    setRun(null);
    await onSaved(result);
  };

  const start = (mode: CsvImportMode) => {
    if (!file || !plan) return;
    if (mode === "preview") setPreview(null);
    setSaved(null);
    void execute({
      mode,
      importId: crypto.randomUUID(),
      fileName: file.name,
      fileSize: file.size,
      plan,
      nextBatch: 0,
      result: configRef.current.emptyResult(mode === "preview"),
      sentLines: 0,
      status: "running",
      error: null,
    });
  };

  const resume = () => {
    if (run && run.status !== "running") void execute(run);
  };

  /** Para a prévia e guarda onde parou. A gravação não é cancelada pela tela: vai até o fim ou para com erro. */
  const cancel = () => {
    if (run?.status !== "running" || run.mode !== "preview") return;
    stopCurrent();
    setRun({ ...run, status: "cancelled", error: null });
  };

  /** Volta ao início: sem arquivo, prévia nem resultado. */
  const clear = () => {
    stopCurrent();
    setRun(null);
    setPreview(null);
    setSaved(null);
    setFileError(null);
    setFile(null);
    setPlan(null);
  };

  const selectFile = async (selected: File | null) => {
    // Trocar o arquivo invalida a prévia e para a execução em andamento.
    clear();
    if (!selected) return;
    const error = validateImportFile(selected);
    if (error) {
      setFileError(error);
      return;
    }
    const nextPlan = configRef.current.plan(await selected.text());
    if (!nextPlan || nextPlan.totalLines === 0) {
      setFileError("O arquivo não tem linhas de dados.");
      return;
    }
    setFile(selected);
    setPlan(nextPlan);
  };

  return {
    file,
    plan,
    fileError,
    run,
    preview,
    saved,
    isRunning: run?.status === "running",
    selectFile,
    start,
    resume,
    cancel,
    clear,
  };
}

export type CsvBatchImportState<TResult, TPlan extends CsvImportPlan> = ReturnType<typeof useCsvBatchImport<TResult, TPlan>>;
